import { useMemo, useState } from "react";
import { Icon } from "@/components/primitives";
import { useScans, type Scan } from "@/hooks/useFigData";
import { toast } from "@/lib/toast";
import { useTierFeatures } from "@/hooks/useTierFeatures";

/**
 * Top-toolbar button on the owner's Profile that exports every sold scan to a
 * CSV — title, brand, category, size, marketplace, sold price, asking price,
 * date sold, days-to-sell. Pure client-side: we already have the full scans
 * collection in memory via useScans(), so no extra round trip is needed.
 *
 * Kept tiny on purpose: this is the v1 ask. Date-range filtering, totals
 * rows, and cost-basis live in v2 once we know people actually use it.
 */

const CSV_HEADERS = [
  "Date sold",
  "Title",
  "Brand",
  "Category",
  "Size",
  "Marketplace",
  "Sold price",
  "Asking price",
  "Days to sell",
] as const;

function csvEscape(value: string | number | null | undefined): string {
  if (value == null) return "";
  const s = String(value);
  // RFC 4180: wrap in quotes if it contains comma, quote, or newline; double up internal quotes.
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function daysBetween(a: string | null | undefined, b: string | null | undefined): number | null {
  if (!a || !b) return null;
  const da = new Date(a).getTime();
  const db = new Date(b).getTime();
  if (!Number.isFinite(da) || !Number.isFinite(db)) return null;
  return Math.max(0, Math.round((db - da) / (1000 * 60 * 60 * 24)));
}

interface ExportSalesButtonProps {
  /** "sm" matches the condensed toolbar variant (28px box, 14px icon).
   *  Default "md" preserves the existing 32 / 16 live styling. */
  size?: "sm" | "md";
}

export function ExportSalesButton({ size = "md" }: ExportSalesButtonProps = {}) {
  const _box = size === "sm" ? 28 : 32;
  const _iconPx = size === "sm" ? 14 : 16;
  const _padPx = size === "sm" ? 4 : 6;
  const { scans } = useScans();
  const { features } = useTierFeatures();
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const soldScans = useMemo(
    () => scans.filter((s) => Boolean(s.sold_at) || /sold/i.test(String(s.status ?? ""))),
    [scans],
  );

  // Build the rows once — used both for the preview table and the CSV. Keeping
  // a single source of truth means "what you see is what you download".
  const previewRows = useMemo(() => {
    return soldScans.map((s: Scan) => {
      const soldAt = s.sold_at ?? null;
      const createdAt = s.created_at ?? null;
      const dateSold = soldAt ? new Date(soldAt).toISOString().slice(0, 10) : "";
      return [
        dateSold,
        s.title ?? "",
        s.brand ?? "",
        s.category ?? "",
        s.size ?? "",
        s.posted_to ?? "",
        s.sold_price != null ? Number(s.sold_price).toFixed(2) : "",
        s.price_suggested != null ? Number(s.price_suggested).toFixed(2) : "",
        daysBetween(createdAt, soldAt) != null ? String(daysBetween(createdAt, soldAt)) : "",
      ] as string[];
    });
  }, [soldScans]);

  const openPreview = () => {
    if (busy) return;
    if (!features.canExportSalesCsv) {
      toast("Sales CSV export is on the Seller plan", {
        description: "Upgrade to download a tax-ready report of every sold piece.",
      });
      return;
    }
    if (!soldScans.length) {
      toast("No sold pieces yet", { description: "Mark a listing as sold to start your tax report." });
      return;
    }
    setPreviewOpen(true);
  };

  const handleExport = () => {
    if (busy) return;
    if (!soldScans.length) return;
    setBusy(true);
    try {
      const csv = [CSV_HEADERS, ...previewRows]
        .map((row) => row.map(csvEscape).join(","))
        .join("\r\n");

      const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `fig-sales-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke a tick so the browser can start the download.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Exported ${soldScans.length} sold ${soldScans.length === 1 ? "piece" : "pieces"}`);
      setPreviewOpen(false);
    } catch (err) {
      console.error("Export failed", err);
      toast.error("Couldn't export your sales");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <button
      onClick={openPreview}
      disabled={busy}
      aria-label="Preview & export sales CSV"
      title="Preview & export sales (CSV)"
      style={{
        background: "none", border: "1px solid hsl(var(--ink))",
        padding: _padPx, cursor: busy ? "wait" : "pointer", color: "hsl(var(--ink))",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: _box, height: _box, position: "relative",
        opacity: busy ? 0.6 : 1,
      }}
    >
      {Icon.share(_iconPx)}
    </button>

    {previewOpen && (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Export preview"
        onClick={() => !busy && setPreviewOpen(false)}
        style={{
          position: "fixed", inset: 0, zIndex: 1000,
          background: "hsl(var(--ink) / 0.45)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 16,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "hsl(var(--paper-2, var(--cream)))",
            border: "1.5px solid hsl(var(--ink))",
            width: "100%", maxWidth: 720, maxHeight: "85vh",
            display: "flex", flexDirection: "column",
            color: "hsl(var(--ink))",
          }}
        >
          {/* Header */}
          <div style={{
            padding: "14px 16px",
            borderBottom: "1px solid hsl(var(--sand-deep))",
            display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12,
          }}>
            <div>
              <div className="caps" style={{ color: "hsl(var(--ink-2))", fontSize: 10, letterSpacing: "0.12em" }}>
                Export preview
              </div>
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 22, lineHeight: 1.1, marginTop: 2 }}>
                {previewRows.length} sold {previewRows.length === 1 ? "piece" : "pieces"}
              </div>
            </div>
            <button
              onClick={() => setPreviewOpen(false)}
              aria-label="Close preview"
              style={{
                background: "transparent", border: "1px dashed hsl(var(--ink-2))",
                padding: "4px 10px", cursor: "pointer",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                letterSpacing: "0.1em", textTransform: "uppercase",
                color: "hsl(var(--ink-2))",
              }}
            >Close</button>
          </div>

          {/* Scrollable table */}
          <div style={{ overflow: "auto", flex: 1 }}>
            <table style={{
              width: "100%", borderCollapse: "collapse",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
              color: "hsl(var(--ink))",
            }}>
              <thead style={{ position: "sticky", top: 0, background: "hsl(var(--cream))" }}>
                <tr>
                  {CSV_HEADERS.map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left", padding: "8px 10px",
                        borderBottom: "1px solid hsl(var(--ink))",
                        fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}
                    >{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 ? "hsl(var(--cream))" : "transparent" }}>
                    {row.map((cell, j) => (
                      <td
                        key={j}
                        style={{
                          padding: "6px 10px",
                          borderBottom: "1px dashed hsl(var(--sand-deep))",
                          whiteSpace: "nowrap",
                          color: cell === "" ? "hsl(var(--ink-2))" : "hsl(var(--ink))",
                        }}
                      >{cell === "" ? "—" : cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div style={{
            padding: "12px 16px",
            borderTop: "1px solid hsl(var(--sand-deep))",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 12, color: "hsl(var(--ink-2))",
            }}>
              Empty cells (—) export as blank in the CSV.
            </div>
            <button
              onClick={handleExport}
              disabled={busy}
              style={{
                // Same contrast trap as the Mark-as-sold button —
                // --marigold + --ink collides on Trapper Keeper / Velvet
                // Underground. The auto-derived --marigold-fg is the safe
                // text color for any --marigold surface across all themes.
                background: "hsl(var(--marigold))",
                color: "hsl(var(--marigold-fg))", border: "1.5px solid hsl(var(--ink))",
                padding: "10px 16px", cursor: busy ? "wait" : "pointer",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600,
                opacity: busy ? 0.6 : 1,
              }}
            >{busy ? "Exporting…" : "Download CSV"}</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}